import { State, Selector, Action, StateContext } from '@ngxs/store';
import {
    AddDriverTrackingResponse,
    RegisterDriverResponse,
    SendConsignmentStatusResponse,
    ReportParcelCountDiscrepancyResponse,
    DriverTrackingClient
} from '@core/services/api.service';
import { Injectable } from '@angular/core';
import { tap, catchError } from 'rxjs/operators';
import {
    AddDriverTrackingAction,
    AddDriverTrackingSuccessAction,
    AddDriverTrackingFailAction,
    RegisterDriverAction,
    RegisterDriverSuccessAction,
    RegisterDriverFailAction,
    SendConsignmentStatusAction,
    SendConsignmentStatusSuccessAction,
    SendConsignmentStatusFailAction,
    ReportParcelCountDiscrepancyAction,
    ReportParcelCountDiscrepancySuccessAction,
    ReportParcelCountDiscrepancyFailAction
} from './driver-tracking.actions';

export class DriverTrackingStateModel {
    public isLoading: boolean;
    public addDriverTrackingResponse: AddDriverTrackingResponse | null;
    public isRegisteringDriver: boolean;
    public registerDriverResponse: RegisterDriverResponse | null;
    public isSendingConsignmentStatus: boolean;
    public sendConsignmentStatusResponse: SendConsignmentStatusResponse | null;
    public isReportingParcelCountDiscrepancy: boolean;
    public reportParcelCountDiscrepancyResponse: ReportParcelCountDiscrepancyResponse | null;
    public error: any;
}

@State<DriverTrackingStateModel>({
    name: 'DriverTracking',
    defaults: {
        isLoading: false,
        addDriverTrackingResponse: null,
        isRegisteringDriver: false,
        registerDriverResponse: null,
        isSendingConsignmentStatus: false,
        sendConsignmentStatusResponse: null,
        isReportingParcelCountDiscrepancy: false,
        reportParcelCountDiscrepancyResponse: null,
        error: null
    }
})
@Injectable()
export class DriverTrackingState {
    constructor(private driverTrackingService: DriverTrackingClient) { }

    @Selector()
    static getIsLoading(state: DriverTrackingStateModel): boolean {
        return state.isLoading;
    }

    @Selector()
    static getAddDriverTrackingResponse(state: DriverTrackingStateModel): AddDriverTrackingResponse | null {
        return state.addDriverTrackingResponse;
    }

    @Selector()
    static getError(state: DriverTrackingStateModel): any {
        return state.error;
    }

    @Selector()
    static getIsRegisteringDriver(state: DriverTrackingStateModel): boolean {
        return state.isRegisteringDriver;
    }

    @Selector()
    static getRegisterDriverResponse(state: DriverTrackingStateModel): RegisterDriverResponse | null {
        return state.registerDriverResponse;
    }

    @Selector()
    static getIsSendingConsignmentStatus(state: DriverTrackingStateModel): boolean {
        return state.isSendingConsignmentStatus;
    }

    @Selector()
    static getSendConsignmentStatusResponse(state: DriverTrackingStateModel): SendConsignmentStatusResponse | null {
        return state.sendConsignmentStatusResponse;
    }

    @Selector()
    static getIsReportingParcelCountDiscrepancy(state: DriverTrackingStateModel): boolean {
        return state.isReportingParcelCountDiscrepancy;
    }

    @Selector()
    static getReportParcelCountDiscrepancyResponse(state: DriverTrackingStateModel): ReportParcelCountDiscrepancyResponse | null {
        return state.reportParcelCountDiscrepancyResponse;
    }

    @Action(AddDriverTrackingAction)
    addDriverTrackingAction({ dispatch, patchState }: StateContext<DriverTrackingStateModel>, action: AddDriverTrackingAction) {
        patchState({ isLoading: true, error: null });

        return this.driverTrackingService.addDriverTracking(action.command).pipe(
            tap((response) => {
                dispatch(new AddDriverTrackingSuccessAction({ response }));
            }),
            catchError(error => {
                dispatch(new AddDriverTrackingFailAction(error));
                throw error;
            })
        );
    }

    @Action(AddDriverTrackingSuccessAction)
    addDriverTrackingSuccessAction({ patchState }: StateContext<DriverTrackingStateModel>, action: AddDriverTrackingSuccessAction) {
        patchState({
            isLoading: false,
            addDriverTrackingResponse: action.payload.response,
            error: null
        });
    }

    @Action(AddDriverTrackingFailAction)
    addDriverTrackingFailAction({ patchState }: StateContext<DriverTrackingStateModel>, action: AddDriverTrackingFailAction) {
        patchState({
            isLoading: false,
            error: action.error
        });
    }

    @Action(RegisterDriverAction)
    registerDriverAction({ dispatch, patchState }: StateContext<DriverTrackingStateModel>, action: RegisterDriverAction) {
        patchState({ isRegisteringDriver: true, error: null });

        return this.driverTrackingService.registerDriver(action.command).pipe(
            tap((response) => {
                dispatch(new RegisterDriverSuccessAction({ response }));
            }),
            catchError(error => {
                dispatch(new RegisterDriverFailAction(error));
                throw error;
            })
        );
    }

    @Action(RegisterDriverSuccessAction)
    registerDriverSuccessAction({ patchState }: StateContext<DriverTrackingStateModel>, action: RegisterDriverSuccessAction) {
        patchState({
            isRegisteringDriver: false,
            registerDriverResponse: action.payload.response,
            error: null
        });
    }

    @Action(RegisterDriverFailAction)
    registerDriverFailAction({ patchState }: StateContext<DriverTrackingStateModel>, action: RegisterDriverFailAction) {
        patchState({
            isRegisteringDriver: false,
            error: action.error
        });
    }

    @Action(SendConsignmentStatusAction)
    sendConsignmentStatusAction({ dispatch, patchState }: StateContext<DriverTrackingStateModel>, action: SendConsignmentStatusAction) {
        patchState({ isSendingConsignmentStatus: true, error: null });

        return this.driverTrackingService.sendConsignmentStatus(action.command).pipe(
            tap((response) => {
                dispatch(new SendConsignmentStatusSuccessAction({ response }));
            }),
            catchError(error => {
                dispatch(new SendConsignmentStatusFailAction(error));
                throw error;
            })
        );
    }

    @Action(SendConsignmentStatusSuccessAction)
    sendConsignmentStatusSuccessAction({ patchState }: StateContext<DriverTrackingStateModel>, action: SendConsignmentStatusSuccessAction) {
        patchState({
            isSendingConsignmentStatus: false,
            sendConsignmentStatusResponse: action.payload.response,
            error: null
        });
    }

    @Action(SendConsignmentStatusFailAction)
    sendConsignmentStatusFailAction({ patchState }: StateContext<DriverTrackingStateModel>, action: SendConsignmentStatusFailAction) {
        patchState({
            isSendingConsignmentStatus: false,
            error: action.error
        });
    }

    @Action(ReportParcelCountDiscrepancyAction)
    reportParcelCountDiscrepancyAction({ dispatch, patchState }: StateContext<DriverTrackingStateModel>, action: ReportParcelCountDiscrepancyAction) {
        patchState({ isReportingParcelCountDiscrepancy: true, error: null });

        return this.driverTrackingService.reportParcelCountDiscrepancy(action.command).pipe(
            tap((response) => {
                dispatch(new ReportParcelCountDiscrepancySuccessAction({ response }));
            }),
            catchError(error => {
                dispatch(new ReportParcelCountDiscrepancyFailAction(error));
                throw error;
            })
        );
    }

    @Action(ReportParcelCountDiscrepancySuccessAction)
    reportParcelCountDiscrepancySuccessAction({ patchState }: StateContext<DriverTrackingStateModel>, action: ReportParcelCountDiscrepancySuccessAction) {
        patchState({
            isReportingParcelCountDiscrepancy: false,
            reportParcelCountDiscrepancyResponse: action.payload.response,
            error: null
        });
    }

    @Action(ReportParcelCountDiscrepancyFailAction)
    reportParcelCountDiscrepancyFailAction({ patchState }: StateContext<DriverTrackingStateModel>, action: ReportParcelCountDiscrepancyFailAction) {
        patchState({
            isReportingParcelCountDiscrepancy: false,
            error: action.error
        });
    }
}
